/**
 * Multi-Layer Intelligence Scoring Enhancement
 * 
 * SURGICAL ENHANCEMENT - Works alongside existing keyword scoring
 * Adds 7 intelligence layers to dramatically improve relevance accuracy
 * 
 * PHASE 1: Core Multi-Layer System (5 layers)
 * PHASE 2: Critical Enhancements (3 additional layers)
 */

class MultiLayerIntelligence {
  constructor(options = {}) {
    this.options = {
      enableContextValidation: options.enableContextValidation !== false,
      enableGeographicIntelligence: options.enableGeographicIntelligence !== false,
      enableSourceCredibility: options.enableSourceCredibility !== false,
      enableNegativeFiltering: options.enableNegativeFiltering !== false,
      enableEmergencyAuthorities: options.enableEmergencyAuthorities !== false,
      enableAgeDecay: options.enableAgeDecay !== false,
      enableEntityDisambiguation: options.enableEntityDisambiguation !== false,
      
      // Scoring weights (configurable)
      contextValidationWeight: options.contextValidationWeight || 1.0,
      geographicWeight: options.geographicWeight || 1.0,
      sourceCredibilityWeight: options.sourceCredibilityWeight || 1.0,
      negativeFilterWeight: options.negativeFilterWeight || 1.0,
      
      // Age decay settings
      decayStartHours: options.decayStartHours || 12,
      decayRatePerSixHours: options.decayRatePerSixHours || 0.1,
      
      // Geographic distance thresholds (km)
      primaryLocationRadius: options.primaryLocationRadius || 25,
      secondaryLocationRadius: options.secondaryLocationRadius || 100,
      distantLocationRadius: options.distantLocationRadius || 500
    };

    // Universal crisis type definitions
    this.crisisTypeDefinitions = {
      weather: {
        primaryTerms: ['flood', 'storm', 'cyclone', 'tornado', 'hurricane', 'rainfall', 'warning', 'severe weather'],
        entityTerms: ['ses', 'bureau of meteorology', 'bom', 'emergency services', 'evacuation centre'],
        actionTerms: ['rescue', 'search', 'evacuate', 'stranded', 'missing', 'trapped'],
        weights: { primary: 15, entity: 12, action: 10 }
      },
      
      terrorism: {
        primaryTerms: ['attack', 'bombing', 'shooter', 'terrorist', 'explosion', 'incident', 'threat'],
        entityTerms: ['police', 'security', 'counter-terrorism', 'authorities', 'investigation'],
        actionTerms: ['lockdown', 'evacuation', 'manhunt', 'arrested', 'cordoned'],
        weights: { primary: 20, entity: 15, action: 12 }
      },
      
      earthquake: {
        primaryTerms: ['earthquake', 'tremor', 'seismic', 'magnitude', 'richter', 'aftershock', 'quake'],
        entityTerms: ['geological survey', 'seismology', 'emergency services', 'disaster response'],
        actionTerms: ['rescue', 'trapped', 'collapse', 'damage', 'casualties', 'evacuation'],
        weights: { primary: 18, entity: 14, action: 11 }
      },
      
      missing_person: {
        primaryTerms: ['missing', 'disappeared', 'vanished', 'last seen', 'search'],
        entityTerms: ['police', 'family', 'authorities', 'investigators', 'volunteers'],
        actionTerms: ['search', 'found', 'located', 'rescue', 'appeal', 'sighting'],
        weights: { primary: 20, entity: 12, action: 15 }
      },
      
      wildfire: {
        primaryTerms: ['fire', 'wildfire', 'bushfire', 'blaze', 'smoke', 'burning'],
        entityTerms: ['fire service', 'rfs', 'firefighters', 'fire department', 'rural fire'],
        actionTerms: ['evacuation', 'containment', 'suppression', 'rescue', 'threat'],
        weights: { primary: 16, entity: 13, action: 11 }
      }
    };

    // Universal emergency authorities by region
    this.emergencyAuthorities = {
      australia: [
        'ses', 'state emergency service', 'bureau of meteorology', 'bom', 
        'rfs', 'rural fire service', 'nsw police', 'qld police', 'vic police',
        'ambulance service', 'fire and rescue', 'australian federal police'
      ],
      uk: [
        'met office', 'environment agency', 'emergency services', 'police',
        'fire service', 'ambulance service', 'coastguard', 'mountain rescue'
      ],
      usa: [
        'fema', 'national weather service', 'emergency management', 'police department',
        'fire department', 'sheriff', 'state police', 'homeland security'
      ],
      default: [
        'police', 'fire', 'ambulance', 'emergency', 'rescue', 'disaster response'
      ]
    };

    // Universal negative context patterns
    this.negativeContextPatterns = [
      {
        pattern: ['gaza', 'israel', 'palestine', 'middle east'],
        contextTerms: ['conflict', 'war', 'military', 'hamas'],
        penalty: -25,
        exceptions: ['australian', 'visitor', 'resident', 'tourist']
      },
      {
        pattern: ['protest', 'march', 'rally', 'demonstration'],
        contextTerms: ['activist', 'climate', 'political', 'rights'],
        penalty: -20,
        exceptions: ['emergency', 'rescue', 'trapped', 'injured']
      },
      {
        pattern: ['concert', 'festival', 'event', 'show'],
        contextTerms: ['music', 'entertainment', 'performer', 'venue'],
        penalty: -30,
        exceptions: ['cancelled', 'emergency', 'evacuation', 'stampede']
      },
      {
        pattern: ['sports', 'game', 'match', 'championship'],
        contextTerms: ['team', 'player', 'score', 'win'],
        penalty: -25,
        exceptions: ['cancelled', 'emergency', 'crowd', 'injury']
      }
    ];

    console.log('🧠 Multi-Layer Intelligence Engine initialized with', Object.keys(this.options).filter(k => this.options[k] === true).length, 'active layers');
  }

  /**
   * MAIN ENHANCEMENT METHOD
   * Applies all intelligence layers to enhance existing keyword score
   */
  enhanceRelevanceScore(baseScore, baseReasons, article, crisisContext, campaignOptions) {
    let enhancedScore = baseScore;
    let enhancementReasons = [...baseReasons];
    let appliedEnhancements = [];

    console.log(`🧠 MULTI-LAYER ENHANCEMENT: Starting with base score ${Math.round(baseScore)} points`);

    try {
      // LAYER 2: Context Validation Engine
      if (this.options.enableContextValidation) {
        const contextResult = this.validateKeywordContext(article, enhancedScore, crisisContext);
        enhancedScore += contextResult.adjustment * this.options.contextValidationWeight;
        enhancementReasons.push(...contextResult.reasons);
        appliedEnhancements.push(`Context: ${contextResult.adjustment > 0 ? '+' : ''}${Math.round(contextResult.adjustment)}`);
      }

      // LAYER 3: Geographic Intelligence System  
      if (this.options.enableGeographicIntelligence) {
        const geoResult = this.calculateGeographicIntelligence(article, crisisContext, campaignOptions);
        enhancedScore += geoResult.adjustment * this.options.geographicWeight;
        enhancementReasons.push(...geoResult.reasons);
        appliedEnhancements.push(`Geographic: ${geoResult.adjustment > 0 ? '+' : ''}${Math.round(geoResult.adjustment)}`);
      }

      // LAYER 4: Source Credibility Weighting
      if (this.options.enableSourceCredibility) {
        const sourceResult = this.calculateSourceCredibility(article, crisisContext);
        const originalScore = enhancedScore;
        enhancedScore *= sourceResult.multiplier * this.options.sourceCredibilityWeight;
        const adjustment = enhancedScore - originalScore;
        enhancementReasons.push(sourceResult.reason);
        appliedEnhancements.push(`Source: ${adjustment > 0 ? '+' : ''}${Math.round(adjustment)} (${sourceResult.multiplier}x)`);
      }

      // LAYER 5: Negative Context Filtering
      if (this.options.enableNegativeFiltering) {
        const negativeResult = this.applyNegativeContextFiltering(article);
        enhancedScore += negativeResult.penalty * this.options.negativeFilterWeight;
        enhancementReasons.push(...negativeResult.reasons);
        if (negativeResult.penalty !== 0) {
          appliedEnhancements.push(`Negative: ${Math.round(negativeResult.penalty)}`);
        }
      }

      // LAYER 6: Emergency Authority Detection (Phase 2)
      if (this.options.enableEmergencyAuthorities) {
        const authorityResult = this.detectEmergencyAuthorities(article, crisisContext);
        enhancedScore += authorityResult.boost;
        enhancementReasons.push(...authorityResult.reasons);
        if (authorityResult.boost > 0) {
          appliedEnhancements.push(`Authority: +${Math.round(authorityResult.boost)}`);
        }
      }

      // LAYER 7: Article Age Decay Function (Phase 2)
      if (this.options.enableAgeDecay) {
        const ageResult = this.calculateAgeDecayFunction(article);
        const originalScore = enhancedScore;
        enhancedScore *= ageResult.multiplier;
        const adjustment = enhancedScore - originalScore;
        enhancementReasons.push(ageResult.reason);
        if (Math.abs(adjustment) > 0.5) {
          appliedEnhancements.push(`Age: ${adjustment > 0 ? '+' : ''}${Math.round(adjustment)}`);
        }
      }

      // Entity Disambiguation is built into Geographic Intelligence

      const totalAdjustment = enhancedScore - baseScore;
      console.log(`🧠 ENHANCEMENT COMPLETE: ${Math.round(baseScore)} → ${Math.round(enhancedScore)} (${totalAdjustment > 0 ? '+' : ''}${Math.round(totalAdjustment)})`);
      console.log(`🧠 APPLIED LAYERS: ${appliedEnhancements.join(', ') || 'none'}`);

      return {
        enhancedScore: Math.max(0, enhancedScore), // Ensure non-negative
        enhancementReasons: enhancementReasons,
        appliedEnhancements: appliedEnhancements,
        layersProcessed: appliedEnhancements.length
      };

    } catch (error) {
      console.error('❌ Error in multi-layer enhancement:', error);
      return {
        enhancedScore: baseScore, // Fallback to original score
        enhancementReasons: [...baseReasons, 'Enhancement error - using base score'],
        appliedEnhancements: [],
        layersProcessed: 0,
        error: error.message
      };
    }
  }

  /**
   * LAYER 2: Context Validation Engine
   * Ensures keywords appear in actual crisis contexts
   */
  validateKeywordContext(article, currentScore, crisisContext) {
    const title = (article.title || '').toLowerCase();
    const content = (article.content || '').toLowerCase();
    const text = `${title} ${content}`;

    let adjustment = 0;
    let reasons = [];

    // Only validate if there's a reasonable base score to work with
    if (currentScore < 5) {
      return { adjustment: 0, reasons: ['Context validation skipped - low base score'] };
    }

    try {
      // Determine crisis type from context or guess from current scoring
      const crisisType = this.determineCrisisType(crisisContext, text);
      const crisisDefinition = this.crisisTypeDefinitions[crisisType];

      if (!crisisDefinition) {
        return { adjustment: 0, reasons: ['Context validation skipped - unknown crisis type'] };
      }

      // Check if high-value keywords have proper crisis context
      const contextValidationResult = this.validateCrisisTermContext(text, crisisDefinition);
      
      if (contextValidationResult.hasStrongContext) {
        adjustment += 15;
        reasons.push(`Strong ${crisisType} context validated (+15)`);
      } else if (contextValidationResult.hasWeakContext) {
        adjustment += 5;
        reasons.push(`Weak ${crisisType} context found (+5)`);
      } else if (contextValidationResult.hasWrongContext) {
        adjustment -= 20;
        reasons.push(`Wrong context detected - ${contextValidationResult.wrongContext} (-20)`);
      }

      // Check for keyword co-occurrence requirements
      const cooccurrenceResult = this.checkKeywordCooccurrence(text);
      adjustment += cooccurrenceResult.adjustment;
      reasons.push(...cooccurrenceResult.reasons);

    } catch (error) {
      console.error('Error in context validation:', error);
      reasons.push('Context validation error');
    }

    return { adjustment, reasons };
  }

  /**
   * Determine most likely crisis type from context
   */
  determineCrisisType(crisisContext, text) {
    // First try to get from crisis context
    if (crisisContext?.type && this.crisisTypeDefinitions[crisisContext.type]) {
      return crisisContext.type;
    }

    // Try to infer from article content
    let maxScore = 0;
    let bestType = 'weather'; // Default fallback

    Object.entries(this.crisisTypeDefinitions).forEach(([type, definition]) => {
      let score = 0;
      definition.primaryTerms.forEach(term => {
        if (text.includes(term)) score += 3;
      });
      definition.entityTerms.forEach(term => {
        if (text.includes(term)) score += 2;
      });
      definition.actionTerms.forEach(term => {
        if (text.includes(term)) score += 1;
      });

      if (score > maxScore) {
        maxScore = score;
        bestType = type;
      }
    });

    return bestType;
  }

  /**
   * Validate that crisis terms appear in proper context
   */
  validateCrisisTermContext(text, crisisDefinition) {
    const strongContextIndicators = [...crisisDefinition.entityTerms, ...crisisDefinition.actionTerms];
    const primaryTermsFound = crisisDefinition.primaryTerms.filter(term => text.includes(term));
    
    if (primaryTermsFound.length === 0) {
      return { hasStrongContext: false, hasWeakContext: false, hasWrongContext: false };
    }

    // Check if primary terms appear with supporting context
    const hasStrongSupport = strongContextIndicators.some(indicator => text.includes(indicator));
    
    // Check for wrong context patterns
    const wrongContextPatterns = {
      'evacuation': ['protest', 'political', 'march', 'demonstration'],
      'missing': ['concert', 'game', 'show', 'missing out'],
      'emergency': ['drill', 'exercise', 'training', 'simulation'],
      'warning': ['spoiler', 'content warning', 'language warning']
    };

    let wrongContext = null;
    for (const term of primaryTermsFound) {
      const wrongPatterns = wrongContextPatterns[term] || [];
      for (const pattern of wrongPatterns) {
        if (text.includes(pattern)) {
          wrongContext = pattern;
          break;
        }
      }
      if (wrongContext) break;
    }

    return {
      hasStrongContext: hasStrongSupport && !wrongContext,
      hasWeakContext: !hasStrongSupport && !wrongContext,
      hasWrongContext: !!wrongContext,
      wrongContext: wrongContext
    };
  }

  /**
   * Check keyword co-occurrence requirements
   */
  checkKeywordCooccurrence(text) {
    let adjustment = 0;
    let reasons = [];

    // Evacuation must have emergency context
    if (text.includes('evacuation') || text.includes('evacuate')) {
      const hasEmergencyContext = ['emergency', 'rescue', 'flood', 'fire', 'danger', 'warning', 'storm'].some(term => text.includes(term));
      const hasNonEmergencyContext = ['protest', 'political', 'crowd control', 'march'].some(term => text.includes(term));
      
      if (hasEmergencyContext && !hasNonEmergencyContext) {
        adjustment += 10;
        reasons.push('Evacuation with emergency context (+10)');
      } else if (hasNonEmergencyContext) {
        adjustment -= 15;
        reasons.push('Evacuation in non-emergency context (-15)');
      }
    }

    // Missing must have person/search context
    if (text.includes('missing')) {
      const hasPersonContext = ['person', 'search', 'found', 'last seen', 'family', 'police'].some(term => text.includes(term));
      const hasNonPersonContext = ['missing out', 'missing:', 'missing the'].some(term => text.includes(term));
      
      if (hasPersonContext && !hasNonPersonContext) {
        adjustment += 8;
        reasons.push('Missing person context validated (+8)');
      } else if (hasNonPersonContext) {
        adjustment -= 10;
        reasons.push('Missing in non-person context (-10)');
      }
    }

    return { adjustment, reasons };
  }

  /**
   * LAYER 3: Geographic Intelligence System
   * Advanced location relevance with entity disambiguation
   */
  calculateGeographicIntelligence(article, crisisContext, campaignOptions) {
    const title = (article.title || '').toLowerCase();
    const content = (article.content || '').toLowerCase();
    const text = `${title} ${content}`;

    let adjustment = 0;
    let reasons = [];

    try {
      // Extract locations from article
      const extractedLocations = this.extractLocationsFromText(text);
      
      if (extractedLocations.length === 0) {
        return { adjustment: 0, reasons: ['No locations detected'] };
      }

      // Get crisis location context
      const crisisLocation = this.getCrisisLocationContext(crisisContext, campaignOptions);
      
      if (!crisisLocation.primary) {
        return { adjustment: 0, reasons: ['No crisis location configured'] };
      }

      // Calculate geographic relevance for each location
      let maxRelevance = -50; // Start with penalty assumption
      let bestLocation = null;

      for (const location of extractedLocations) {
        const relevance = this.calculateLocationRelevance(location, crisisLocation, text);
        if (relevance.score > maxRelevance) {
          maxRelevance = relevance.score;
          bestLocation = relevance;
        }
      }

      if (bestLocation) {
        adjustment = maxRelevance;
        reasons.push(bestLocation.reason);
        
        // Apply entity disambiguation
        const disambiguationResult = this.applyEntityDisambiguation(bestLocation.location, text, crisisLocation);
        adjustment += disambiguationResult.adjustment;
        reasons.push(...disambiguationResult.reasons);
      }

    } catch (error) {
      console.error('Error in geographic intelligence:', error);
      reasons.push('Geographic analysis error');
    }

    return { adjustment, reasons };
  }

  /**
   * Extract location mentions from text
   */
  extractLocationsFromText(text) {
    // Comprehensive location patterns (expandable)
    const locationPatterns = [
      // Australian locations
      'sydney', 'melbourne', 'brisbane', 'perth', 'adelaide', 'darwin', 'hobart', 'canberra',
      'newcastle', 'gold coast', 'sunshine coast', 'townsville', 'cairns', 'toowoomba',
      'hunter valley', 'hunter', 'cessnock', 'maitland', 'singleton', 'muswellbrook',
      'nsw', 'new south wales', 'qld', 'queensland', 'vic', 'victoria', 'sa', 'south australia',
      'wa', 'western australia', 'tas', 'tasmania', 'nt', 'northern territory', 'act',
      
      // International major cities
      'london', 'manchester', 'birmingham', 'liverpool', 'leeds', 'glasgow', 'edinburgh',
      'new york', 'los angeles', 'chicago', 'houston', 'phoenix', 'philadelphia', 'san antonio',
      'tokyo', 'osaka', 'kyoto', 'yokohama', 'nagoya', 'sapporo', 'fukuoka',
      'paris', 'marseille', 'lyon', 'toulouse', 'nice', 'nantes', 'strasbourg',
      'berlin', 'hamburg', 'munich', 'cologne', 'frankfurt', 'stuttgart', 'dusseldorf',
      
      // International regions/countries  
      'united kingdom', 'uk', 'england', 'scotland', 'wales', 'ireland',
      'united states', 'usa', 'america', 'california', 'texas', 'florida', 'new york state',
      'japan', 'china', 'india', 'germany', 'france', 'italy', 'spain', 'canada',
      'gaza', 'israel', 'palestine', 'syria', 'iraq', 'afghanistan', 'ukraine', 'russia'
    ];

    const foundLocations = [];
    locationPatterns.forEach(pattern => {
      if (text.includes(pattern)) {
        foundLocations.push(pattern);
      }
    });

    return [...new Set(foundLocations)]; // Remove duplicates
  }

  /**
   * Get crisis location context from various sources
   */
  getCrisisLocationContext(crisisContext, campaignOptions) {
    const context = {
      primary: null,
      related: [],
      country: null,
      region: null
    };

    // Try to get from crisis context first
    if (crisisContext?.primaryLocation) {
      context.primary = crisisContext.primaryLocation.toLowerCase();
      context.related = (crisisContext.relatedLocations || []).map(l => l.toLowerCase());
      context.country = crisisContext.country?.toLowerCase();
      context.region = crisisContext.region?.toLowerCase();
    }
    // Fallback to campaign options
    else if (campaignOptions?.locations?.length > 0) {
      context.primary = campaignOptions.locations[0].toLowerCase();
      context.related = campaignOptions.locations.slice(1).map(l => l.toLowerCase());
      
      // Infer country/region from primary location
      const locationMapping = this.inferCountryFromLocation(context.primary);
      context.country = locationMapping.country;
      context.region = locationMapping.region;
    }

    return context;
  }

  /**
   * Calculate relevance score for a specific location
   */
  calculateLocationRelevance(location, crisisLocation, text) {
    const loc = location.toLowerCase();
    const crisis = crisisLocation.primary?.toLowerCase();
    
    // Primary crisis location match
    if (loc === crisis || (crisis && loc.includes(crisis)) || (crisis && crisis.includes(loc))) {
      return { 
        score: 25, 
        location: location,
        reason: `Primary crisis location match: ${location} (+25)` 
      };
    }

    // Related location match
    if (crisisLocation.related.some(related => 
        loc === related || loc.includes(related) || related.includes(loc))) {
      return { 
        score: 15, 
        location: location,
        reason: `Related location match: ${location} (+15)` 
      };
    }

    // Same country/region
    if (crisisLocation.country && (loc === crisisLocation.country || loc.includes(crisisLocation.country))) {
      return { 
        score: 5, 
        location: location,
        reason: `Same country: ${location} (+5)` 
      };
    }

    // Check if it's a known irrelevant location
    const irrelevantScore = this.calculateIrrelevantLocationPenalty(location, crisisLocation, text);
    if (irrelevantScore.penalty < 0) {
      return {
        score: irrelevantScore.penalty,
        location: location,
        reason: irrelevantScore.reason
      };
    }

    // Neutral location
    return { 
      score: 0, 
      location: location,
      reason: `Neutral location: ${location} (0)` 
    };
  }

  /**
   * Calculate penalties for irrelevant locations
   */
  calculateIrrelevantLocationPenalty(location, crisisLocation, text) {
    const loc = location.toLowerCase();

    // International conflicts (when crisis is domestic)
    const conflictLocations = ['gaza', 'israel', 'palestine', 'syria', 'iraq', 'afghanistan', 'ukraine', 'russia'];
    if (conflictLocations.includes(loc) && crisisLocation.country !== loc) {
      // Check for local connection
      const hasLocalConnection = this.checkForLocalConnection(text, crisisLocation);
      if (!hasLocalConnection) {
        return { 
          penalty: -25, 
          reason: `Irrelevant international location: ${location} (-25)` 
        };
      }
    }

    // Major cities outside crisis area
    const majorCities = ['sydney', 'melbourne', 'brisbane', 'perth', 'london', 'new york', 'tokyo', 'paris'];
    if (majorCities.includes(loc) && !this.isLocationRelevantToCrisis(loc, crisisLocation)) {
      return { 
        penalty: -8, 
        reason: `Major city outside crisis area: ${location} (-8)` 
      };
    }

    return { penalty: 0, reason: null };
  }

  /**
   * Check for local connection in text
   */
  checkForLocalConnection(text, crisisLocation) {
    const connectionTerms = [
      'resident', 'local', 'visiting', 'tourist', 'from', 'lives in', 'home to',
      ...(crisisLocation.related || []),
      crisisLocation.primary
    ].filter(Boolean);

    return connectionTerms.some(term => text.includes(term.toLowerCase()));
  }

  /**
   * Check if location is relevant to crisis
   */
  isLocationRelevantToCrisis(location, crisisLocation) {
    const allRelevantLocations = [
      crisisLocation.primary,
      ...(crisisLocation.related || []),
      crisisLocation.region,
      crisisLocation.country
    ].filter(Boolean).map(l => l.toLowerCase());

    return allRelevantLocations.some(relevant => 
      location.includes(relevant) || relevant.includes(location)
    );
  }

  /**
   * Apply entity disambiguation (Phase 2 enhancement)
   */
  applyEntityDisambiguation(location, text, crisisLocation) {
    let adjustment = 0;
    let reasons = [];

    // Handle ambiguous city names
    const ambiguousLocations = {
      'newcastle': {
        australia: ['nsw', 'australia', 'hunter', 'ses'],
        uk: ['england', 'uk', 'tyne', 'northumberland']
      },
      'richmond': {
        australia: ['melbourne', 'victoria', 'vic', 'australia'],
        uk: ['london', 'england', 'uk', 'richmond upon thames'],
        usa: ['virginia', 'usa', 'america']
      },
      'cambridge': {
        australia: ['tasmania', 'tas', 'australia'],
        uk: ['england', 'uk', 'university'],
        usa: ['massachusetts', 'harvard', 'mit']
      }
    };

    const loc = location.toLowerCase();
    if (ambiguousLocations[loc]) {
      const disambiguationOptions = ambiguousLocations[loc];
      let matchedRegion = null;

      // Check which region this location refers to
      Object.entries(disambiguationOptions).forEach(([region, indicators]) => {
        if (indicators.some(indicator => text.includes(indicator))) {
          matchedRegion = region;
        }
      });

      if (matchedRegion) {
        // Check if this matches crisis location region
        const crisisCountry = crisisLocation.country || this.inferCountryFromLocation(crisisLocation.primary).country;
        
        if (matchedRegion === crisisCountry) {
          adjustment += 10;
          reasons.push(`Entity disambiguation: ${location} confirmed as ${crisisCountry} (+10)`);
        } else {
          adjustment -= 15;
          reasons.push(`Entity disambiguation: ${location} is ${matchedRegion}, not ${crisisCountry} (-15)`);
        }
      }
    }

    return { adjustment, reasons };
  }

  /**
   * Infer country from location name
   */
  inferCountryFromLocation(location) {
    if (!location) return { country: null, region: null };
    
    const loc = location.toLowerCase();
    
    // Australian locations
    if (['hunter', 'cessnock', 'newcastle', 'sydney', 'melbourne', 'brisbane', 'nsw', 'qld', 'vic'].some(term => loc.includes(term))) {
      return { country: 'australia', region: loc.includes('nsw') ? 'nsw' : null };
    }
    
    // UK locations
    if (['london', 'manchester', 'birmingham', 'england', 'scotland', 'wales'].some(term => loc.includes(term))) {
      return { country: 'uk', region: 'england' };
    }
    
    // US locations
    if (['new york', 'california', 'texas', 'florida', 'usa', 'america'].some(term => loc.includes(term))) {
      return { country: 'usa', region: null };
    }
    
    return { country: null, region: null };
  }

  /**
   * LAYER 4: Source Credibility Weighting
   * Weight articles by source type and locality
   */
  calculateSourceCredibility(article, crisisContext) {
    const source = (article.source || '').toLowerCase();
    
    // Determine if this is a local source for the crisis
    const isLocalSource = this.isLocalSource(source, crisisContext);
    
    // Source type classification
    let multiplier = 1.0;
    let reason = 'Source credibility: ';

    if (isLocalSource) {
      multiplier = 1.5;
      reason += `Local news source (${multiplier}x)`;
    } else if (this.isNationalNews(source)) {
      multiplier = 1.2;
      reason += `National news source (${multiplier}x)`;
    } else if (this.isMajorMetro(source)) {
      multiplier = 1.0;
      reason += `Major metro source (${multiplier}x)`;
    } else if (this.isInternationalNews(source)) {
      multiplier = 0.8;
      reason += `International source (${multiplier}x)`;
    } else if (this.isSocialMedia(source)) {
      multiplier = 0.7;
      reason += `Social media source (${multiplier}x)`;
    } else {
      multiplier = 0.9;
      reason += `Unknown source type (${multiplier}x)`;
    }

    return { multiplier, reason };
  }

  /**
   * Check if source is local to crisis area
   */
  isLocalSource(source, crisisContext) {
    if (!crisisContext) return false;
    
    const localIndicators = [
      crisisContext.primaryLocation,
      ...(crisisContext.relatedLocations || []),
      crisisContext.region
    ].filter(Boolean).map(l => l.toLowerCase());

    // Check for local news patterns
    const localPatterns = [
      'herald', 'chronicle', 'gazette', 'times', 'news', 'daily',
      ...localIndicators
    ];

    return localPatterns.some(pattern => source.includes(pattern)) &&
           localIndicators.some(indicator => source.includes(indicator));
  }

  /**
   * Check if source is national news
   */
  isNationalNews(source) {
    const nationalSources = [
      'abc news', 'sbs news', 'bbc news', 'cnn', 'reuters', 'associated press',
      'national public radio', 'npr', 'pbs', 'sky news'
    ];
    
    return nationalSources.some(national => source.includes(national));
  }

  /**
   * Check if source is major metro
   */
  isMajorMetro(source) {
    const metroSources = [
      'sydney morning herald', 'smh', 'the age', 'herald sun', 'courier mail',
      'the guardian', 'the times', 'telegraph', 'independent', 'new york times',
      'washington post', 'wall street journal', 'usa today'
    ];
    
    return metroSources.some(metro => source.includes(metro));
  }

  /**
   * Check if source is international news
   */
  isInternationalNews(source) {
    const internationalSources = [
      'al jazeera', 'france 24', 'deutsche welle', 'rt', 'xinhua',
      'tass', 'itar', 'press tv', 'presstv'
    ];
    
    return internationalSources.some(intl => source.includes(intl));
  }

  /**
   * Check if source is social media
   */
  isSocialMedia(source) {
    const socialSources = ['twitter', 'facebook', 'instagram', 'tiktok', 'reddit', 'youtube'];
    return socialSources.some(social => source.includes(social));
  }

  /**
   * LAYER 5: Negative Context Filtering
   * Detect and penalize obviously irrelevant content
   */
  applyNegativeContextFiltering(article) {
    const title = (article.title || '').toLowerCase();
    const content = (article.content || '').toLowerCase();
    const text = `${title} ${content}`;

    let penalty = 0;
    let reasons = [];

    for (const pattern of this.negativeContextPatterns) {
      // Check if article contains pattern terms
      const hasPattern = pattern.pattern.some(term => text.includes(term));
      if (!hasPattern) continue;

      // Check if it has the negative context
      const hasNegativeContext = pattern.contextTerms.some(term => text.includes(term));
      if (!hasNegativeContext) continue;

      // Check for exceptions that would override the penalty
      const hasException = pattern.exceptions.some(exception => text.includes(exception));
      if (hasException) {
        reasons.push(`Negative pattern detected but exception found: ${pattern.pattern[0]} with ${pattern.exceptions.find(e => text.includes(e))}`);
        continue;
      }

      // Apply penalty
      penalty += pattern.penalty;
      const foundPattern = pattern.pattern.find(p => text.includes(p));
      const foundContext = pattern.contextTerms.find(c => text.includes(c));
      reasons.push(`Negative context: ${foundPattern} + ${foundContext} (${pattern.penalty})`);
    }

    return { penalty, reasons };
  }

  /**
   * LAYER 6: Emergency Authority Detection (Phase 2)
   * Boost articles mentioning relevant emergency authorities
   */
  detectEmergencyAuthorities(article, crisisContext) {
    const title = (article.title || '').toLowerCase();
    const content = (article.content || '').toLowerCase();
    const text = `${title} ${content}`;

    let boost = 0;
    let reasons = [];

    // Determine relevant authorities based on crisis location
    const crisisCountry = crisisContext?.country || 
                         this.inferCountryFromLocation(crisisContext?.primaryLocation).country ||
                         'default';
    
    const relevantAuthorities = this.emergencyAuthorities[crisisCountry] || this.emergencyAuthorities.default;

    // Check for authority mentions
    const foundAuthorities = [];
    relevantAuthorities.forEach(authority => {
      if (text.includes(authority)) {
        foundAuthorities.push(authority);
      }
    });

    if (foundAuthorities.length > 0) {
      // Base boost for any emergency authority
      boost += 12;
      
      // Additional boost for multiple authorities
      if (foundAuthorities.length > 1) {
        boost += 6;
      }
      
      // Extra boost for title mentions
      const titleAuthorities = foundAuthorities.filter(auth => title.includes(auth));
      if (titleAuthorities.length > 0) {
        boost += 8;
        reasons.push(`Emergency authorities in title: ${titleAuthorities.join(', ')} (+8)`);
      }
      
      reasons.push(`Emergency authorities detected: ${foundAuthorities.join(', ')} (+${boost - (titleAuthorities.length > 0 ? 8 : 0)})`);
    }

    return { boost, reasons };
  }

  /**
   * LAYER 7: Article Age Decay Function (Phase 2)
   * Apply graduated relevance decay based on article age
   */
  calculateAgeDecayFunction(article) {
    if (!article.publishedAt) {
      return { multiplier: 1.0, reason: 'Age decay: No publish date available' };
    }

    try {
      const publishDate = new Date(article.publishedAt);
      const now = Date.now();
      const ageMs = now - publishDate.getTime();
      const ageHours = ageMs / (1000 * 60 * 60);

      // No decay for very fresh articles
      if (ageHours < this.options.decayStartHours) {
        return { multiplier: 1.0, reason: `Age decay: Fresh article (${ageHours.toFixed(1)}h)` };
      }

      // Check for breaking news indicators that exempt from decay
      const title = (article.title || '').toLowerCase();
      const breakingIndicators = ['breaking', 'urgent', 'developing', 'live', 'update', 'just in'];
      const isBreaking = breakingIndicators.some(indicator => title.includes(indicator));
      
      if (isBreaking) {
        return { multiplier: 1.0, reason: 'Age decay: Breaking news exempt' };
      }

      // Calculate decay multiplier
      const decayPeriods = Math.floor((ageHours - this.options.decayStartHours) / 6); // 6-hour periods
      const decayMultiplier = 1.0 - (decayPeriods * this.options.decayRatePerSixHours);
      const finalMultiplier = Math.max(0.3, decayMultiplier); // Minimum 30% of original score

      const ageInDays = ageHours / 24;
      return { 
        multiplier: finalMultiplier, 
        reason: `Age decay: ${ageInDays.toFixed(1)} days old (${finalMultiplier.toFixed(2)}x)` 
      };

    } catch (error) {
      console.error('Error calculating age decay:', error);
      return { multiplier: 1.0, reason: 'Age decay: Calculation error' };
    }
  }
}

export default MultiLayerIntelligence;